import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useDebounce } from "@uidotdev/usehooks";

import {
  InfiniteSelectBase,
  InfiniteSelectDataHook,
  SelectOption,
} from "@components/Selects/InfiniteSelectBase";
import { UserDocument, useUsersQuery, type UserQuery } from "@graphql/users/users.g";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE = 300;

const useInfiniteUsers = (
  searchQuery: string,
  isOpen: boolean,
  disabled: boolean,
): InfiniteSelectDataHook => {
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE);

  const query = useInfiniteQuery({
    queryKey: ["InfiniteUsers", debouncedSearchQuery],
    queryFn: async ({ pageParam }) => {
      const result = await useUsersQuery.fetcher({
        first: PAGE_SIZE,
        offset: pageParam as number,
        sortBy: "name",
        ...(debouncedSearchQuery && {
          name_Icontains: debouncedSearchQuery,
        }),
      })();
      return result;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.length * PAGE_SIZE;
      const totalCount = lastPage?.users?.totalCount || 0;

      if (currentOffset < totalCount) {
        return currentOffset;
      }
      return undefined;
    },
    enabled: isOpen && !disabled,
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo(() => {
    const allOptions: Array<{ id: string; label: string }> = [];

    if (query.data?.pages) {
      query.data.pages.forEach((page) => {
        const users = page?.users?.edges || [];
        users.forEach((edge) => {
          const node = edge?.node;
          if (node) {
            allOptions.push({
              id: node.id,
              label: node.name || node.email || "",
            });
          }
        });
      });
    }

    return allOptions;
  }, [query.data]);

  return {
    ...query,
    options,
  };
};

type Props = {
  value: string | undefined;
  onChange: (value: string | undefined, option?: SelectOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
};

export const InfiniteUserSelect = ({
  value,
  onChange,
  placeholder = "Search and select user...",
  disabled = false,
}: Props) => {
  const fetchSelectedOption = useCallback(
    async (id: string): Promise<SelectOption | null> => {
      try {
        const response = await fetch(
          import.meta.env.VITE_API_URL_GRAPHQL as string,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              query: UserDocument,
              variables: { userId: id },
            }),
          },
        );

        const json = await response.json();

        if (json.errors) {
          console.error("GraphQL error fetching user:", json.errors);
          return null;
        }

        const data = json.data as UserQuery;
        const user = data?.user;

        if (user) {
          return {
            id: user.id,
            label: user.name || user.email || "",
          };
        }
      } catch (error) {
        console.error("Failed to fetch selected user:", error);
      }
      return null;
    },
    [],
  );

  const useDataWithDisabled = useMemo(() => {
    return (searchQuery: string, isOpen: boolean) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useInfiniteUsers(searchQuery, isOpen, disabled);
    };
  }, [disabled]);

  return (
    <InfiniteSelectBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      useData={useDataWithDisabled}
      fetchSelectedOption={fetchSelectedOption}
      loadingText="Loading users..."
      noResultsText="No users found"
      loadingMoreText="Loading more..."
      disabled={disabled}
    />
  );
};
